import type { ReactNode } from "react"
import { Check, CircleDashed, Clock, X } from "lucide-react"

import { cn } from "@/lib/utils"
import type { AllocationState } from "../process"

type Person = { id: number; nameFa: string } | null | undefined
export type StepperAllocation = {
  status: string
  submittedAt?: string | null
  divApprovedAt?: string | null
  divApprovedBy?: Person
  employee?: { division?: { nameFa: string; head?: Person } | null }
  lines: Array<{ id: number; percentage: string; buApprovedAt?: string | null; buRejectionReason?: string; buApprovedBy?: Person; businessUnit: { nameFa: string; head?: Person } }>
}

type Mark = "done" | "current" | "rejected" | "todo"
const date = (value?: string | null) => (value ? new Date(value).toLocaleDateString("fa-IR", { month: "long", day: "numeric" }) : "")

/** Where one split is in the approval chain: sent → division head → each business → final. */
export function StatusStepper({ allocation }: { allocation: StepperAllocation }) {
  const state = allocation.status as AllocationState
  const sent = state !== "draft" && state !== "missing" && state !== "rejected"
  const divisionDone = state === "div_approved" || state === "approved"
  const rejectedByUnit = state === "rejected" && allocation.lines.some((line) => line.buRejectionReason)
  const divisionHead = allocation.employee?.division?.head?.nameFa

  const steps: Array<{ title: string; mark: Mark; detail?: string; children?: ReactNode }> = [
    {
      title: "ثبت و ارسال",
      mark: sent ? "done" : state === "rejected" ? "rejected" : "current",
      detail: sent ? date(allocation.submittedAt) : state === "rejected" ? "برگشت خورده؛ اصلاح و ارسال مجدد" : "هنوز ارسال نشده",
    },
    {
      title: "تأیید مدیر واحد",
      mark: divisionDone ? "done" : state === "submitted" ? "current" : state === "rejected" && !rejectedByUnit && allocation.submittedAt ? "rejected" : "todo",
      detail: divisionDone
        ? `${allocation.divApprovedBy?.nameFa ?? divisionHead ?? ""}، ${date(allocation.divApprovedAt)}`
        : divisionHead ? `منتظر ${divisionHead}` : "مدیر واحد تعیین نشده است",
    },
    {
      title: "تأیید کسب‌وکارها",
      mark: state === "approved" ? "done" : state === "div_approved" ? "current" : rejectedByUnit ? "rejected" : "todo",
      children: (divisionDone || rejectedByUnit) && (
        <ul className="mt-2 grid gap-1">
          {allocation.lines.map((line) => {
            const mark: Mark = line.buApprovedAt ? "done" : line.buRejectionReason ? "rejected" : "current"
            return (
              <li key={line.id} className="flex items-center gap-2 text-xs">
                <Icon mark={mark} small />
                <span className="font-medium">{line.businessUnit.nameFa}</span>
                <span className="text-muted-foreground tabular-nums">{(Number(line.percentage) * 100).toLocaleString("fa-IR", { maximumFractionDigits: 2 })}٪</span>
                <span className="text-muted-foreground">
                  {line.buApprovedAt ? `${line.buApprovedBy?.nameFa ?? ""}، ${date(line.buApprovedAt)}` : line.buRejectionReason ? `رد: ${line.buRejectionReason}` : line.businessUnit.head ? `منتظر ${line.businessUnit.head.nameFa}` : "مدیر کسب‌وکار تعیین نشده"}
                </span>
              </li>
            )
          })}
        </ul>
      ),
    },
    { title: "تأیید نهایی", mark: state === "approved" ? "done" : "todo" },
  ]

  return (
    <ol className="grid gap-0" aria-label="وضعیت تأیید">
      {steps.map((step, index) => (
        <li key={step.title} className="relative flex gap-3 pb-5 last:pb-0" aria-current={step.mark === "current" ? "step" : undefined}>
          {index < steps.length - 1 && <span aria-hidden className={cn("absolute top-7 bottom-0 right-[13px] w-0.5", step.mark === "done" ? "bg-success" : "bg-border")} />}
          <Icon mark={step.mark} />
          <div className="min-w-0 pt-0.5">
            <p className={cn("text-sm font-medium", step.mark === "todo" && "text-muted-foreground", step.mark === "rejected" && "text-destructive")}>{step.title}</p>
            {step.detail && <p className="text-xs text-muted-foreground">{step.detail}</p>}
            {step.children}
          </div>
        </li>
      ))}
    </ol>
  )
}

function Icon({ mark, small }: { mark: Mark; small?: boolean }) {
  const size = small ? "size-4" : "size-7"
  const inner = small ? "size-3" : "size-4"
  const label = { done: "انجام شد", current: "در جریان", rejected: "برگشت خورده", todo: "در انتظار" }[mark]
  return (
    <span role="img" aria-label={label} className={cn("relative z-10 grid shrink-0 place-items-center rounded-full border", size,
      mark === "done" && "border-transparent bg-success text-white",
      mark === "current" && "border-primary bg-primary/10 text-primary",
      mark === "rejected" && "border-transparent bg-destructive text-white",
      mark === "todo" && "bg-background text-muted-foreground")}>
      {mark === "done" ? <Check className={inner} /> : mark === "rejected" ? <X className={inner} /> : mark === "current" ? <Clock className={inner} /> : <CircleDashed className={inner} />}
    </span>
  )
}
